import { FC, useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import ETextInput from '@/components/everridge/ETextInput';
import ETextAreaInput from '@/components/everridge/ETextAreaInput';
import ESingleSelectPicker from '@/components/everridge/ESingleSelectPicker';
import { Button } from '@/components/ui/button';
import { usePaymentSettingsApi } from '@/features/settings/hooks/usePaymentSettingsApi';
import {
	PaymentProviderType,
	UpdatePaymentSettingsDto,
} from '@/features/settings/types/payment-settings-dto';

type PaymentFormData = {
	provider: PaymentProviderType;
	authUrl?: string;
	apiUrl?: string;
	clientId?: string;
	secret?: string;
	callbackPublicKey?: string;
};

type Props = {
	isMobile?: boolean;
};

const SettingsPaymentDetails: FC<Props> = ({ isMobile }) => {
	const { t } = useTranslation();
	const { paymentSettingsData, updatePaymentSettings, isUpdatingPaymentSettings } =
		usePaymentSettingsApi();

	const { control, handleSubmit, reset } = useForm<PaymentFormData>({
		defaultValues: {
			provider: PaymentProviderType.None,
		},
	});

	useEffect(() => {
		if (paymentSettingsData) {
			reset({
				provider: paymentSettingsData.provider,
				authUrl: paymentSettingsData.authUrl,
				apiUrl: paymentSettingsData.apiUrl,
				clientId: paymentSettingsData.clientIdMasked,
				secret: paymentSettingsData.secretMasked,
				callbackPublicKey: paymentSettingsData.callbackPublicKey,
			});
		}
	}, [paymentSettingsData, reset]);

	const selectedProvider = useWatch({ control, name: 'provider' });

	const providerOptions = [
		{ value: String(PaymentProviderType.None), label: t('main.settings.components.payment.providers.none') },
		{ value: String(PaymentProviderType.Bog), label: t('main.settings.components.payment.providers.bog') },
		{ value: String(PaymentProviderType.Flitt), label: t('main.settings.components.payment.providers.flitt') },
	];

	const handleFormSubmit = async (form: PaymentFormData) => {
		const dto: UpdatePaymentSettingsDto = {
			provider: Number(form.provider),
			authUrl: form.authUrl,
			apiUrl: form.apiUrl,
			clientId: form.clientId,
			secret: form.secret,
			callbackPublicKey: form.callbackPublicKey,
		};

		await updatePaymentSettings(dto);
	};

	const isConfigured = Number(selectedProvider) !== PaymentProviderType.None;

	return (
		<div
			className={cn(
				'w-full p-6 flex flex-col gap-4 border border-popover-100 rounded-2.5xl bg-popover-10',
				isMobile && 'w-full p-0 border-0 bg-popover-30',
			)}
		>
			<div className="flex items-center justify-between gap-4">
				<h3 className={cn(isMobile && 'text-base')}>
					{t('main.settings.components.payment.title')}
				</h3>
				<Button onClick={handleSubmit(handleFormSubmit)} disabled={isUpdatingPaymentSettings}>
					{t('elements.buttons.saveChanges')}
				</Button>
			</div>

			<p className="text-xs font-medium text-popover-800">
				{t('main.settings.components.payment.text')}
			</p>

			<div className="flex flex-col gap-1.5">
				<h4 className="text-sm font-medium">
					{t('main.settings.components.payment.provider')}
				</h4>
				<Controller
					control={control}
					name="provider"
					render={({ field: { value, onChange } }) => (
						<ESingleSelectPicker
							value={value !== undefined && value !== null ? String(value) : null}
							onChange={(next) => onChange(next === null ? PaymentProviderType.None : Number(next))}
							options={providerOptions}
						/>
					)}
				/>
			</div>

			{isConfigured && (
				<>
					<div className={cn('flex gap-4', isMobile && 'flex-col')}>
						<ETextInput
							control={control}
							fieldName="authUrl"
							label={t('main.settings.components.payment.fields.authUrl')}
						/>
						<ETextInput
							control={control}
							fieldName="apiUrl"
							label={t('main.settings.components.payment.fields.apiUrl')}
						/>
					</div>
					<div className={cn('flex gap-4', isMobile && 'flex-col')}>
						<ETextInput
							control={control}
							fieldName="clientId"
							label={t('main.settings.components.payment.fields.clientId')}
						/>
						<ETextInput
							control={control}
							fieldName="secret"
							label={t('main.settings.components.payment.fields.secret')}
						/>
					</div>
					<ETextAreaInput
						control={control}
						fieldName="callbackPublicKey"
						label={t('main.settings.components.payment.fields.callbackPublicKey')}
						placeholder="-----BEGIN PUBLIC KEY-----"
						className="h-40 font-mono whitespace-pre"
					/>
				</>
			)}
		</div>
	);
};

export default SettingsPaymentDetails;
